import { FC, useState, useRef, useCallback, useEffect, useMemo } from 'react'
import styles from 'components/features/pfm/BadgeWithAnnotation/BadgeWithAnnotation.styl'
import Badge from 'components/features/insights/Insight/Badge'
import { RectOffset, getOffset, filterEventsFromNode } from 'helpers/DOMHelpers'
import { ExpandableAnnotation } from 'components/features/pfm/ExpandableAnnotation/ExpandableAnnotation'
import Annotation from 'components/features/insights/Insight/Annotation'
import cn from 'classnames'

type BadgeWithAnnotationProps = {
  type: 'match' | 'reward'
  annotationText?: string
  AnnotationsContainer: FC
}

export const BadgeWithAnnotation: FC<BadgeWithAnnotationProps> = (props) => {
  const { annotationText, children, AnnotationsContainer, type } = props

  const badgeRef = useRef<HTMLDivElement>(null)
  const badgeNode = badgeRef.current
  const [isExpanded, setIsExpanded] = useState(false)

  const handleClick = useCallback(() => {
    if (!annotationText) {
      return
    }
    setIsExpanded(!isExpanded)
  }, [isExpanded, annotationText])

  const handleClose = useCallback(() => {
    setIsExpanded(false)
  }, [])

  const annotationOffsetRef = useRef<RectOffset>(null)
  useEffect(() => {
    if (!badgeNode) {
      return
    }
    annotationOffsetRef.current = getOffset(badgeNode.parentElement, badgeNode)

  }, [badgeNode])

  const annotationChild = useMemo(() => {
    if (!annotationText) {
      return null
    }

    return (
      <AnnotationsContainer>
        <ExpandableAnnotation
          isShown={ isExpanded }
          offset={ annotationOffsetRef.current }
          onClose={ filterEventsFromNode(badgeRef, handleClose) }
        >
          <Annotation
            theme={ type === 'match' ? 'blue' : 'golden' }
            size='responsive'
            isExpanded={ false }
            isModuleInsight={ false }
            title={ type === 'match' ? 'MATCH DETAILS' : 'REWARD DETAILS' }
            markdown={ annotationText }
            onCloseButtonClick={ handleClose }
          />
        </ExpandableAnnotation>
      </AnnotationsContainer>
    )

  }, [isExpanded, handleClose, annotationText, AnnotationsContainer, type])

  return (
    <div ref={ badgeRef } className={ cn(isExpanded && styles.expanded) }>
      { annotationChild }
      <Badge
        theme={ type === 'match' ? 'blue' : 'golden' }
        size='small'
        withChevron={ !!annotationText }
        isExpanded={ isExpanded }
        onClick={ handleClick }
      >
        { children }
      </Badge>
    </div>
  )
}
